// Public PM activity report — /activity/:token (share link from Reports → PM Activity Report). Printable.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, ArrowUUpLeft, Wrench, Camera, Printer } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (d) => d ? new Date(d.length === 10 ? d + "T12:00:00" : d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

function Section({ title, rows, kind, icon: Icon, color }) {
  if (!rows?.length) return null;
  return (
    <section className="mt-6" data-testid={`activity-section-${kind}`}>
      <h2 className="text-xs font-black uppercase tracking-widest inline-flex items-center gap-1" style={{ color }}><Icon weight="fill" size={14}/> {title} ({rows.length})</h2>
      <div className="mt-2 divide-y divide-slate-200 border-2 border-slate-900 bg-white">
        {rows.map((r, i) => (
          <div key={i} className="p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2 font-bold">
              {r.unit && <span className="bg-slate-900 text-white px-1.5 text-[10px] font-black">APT {r.unit}</span>}
              <span>{r.title}</span>
              {(r.external_wo || r.wo) && <span className="font-mono text-[10px] text-slate-500">WO {r.external_wo || r.wo}</span>}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {kind === "completed" && <>Completed {fmt(r.completed)}{r.tech ? ` by ${r.tech}` : ""}{r.notes ? ` — ${r.notes}` : ""}</>}
              {kind === "returning" && <>Started · returning {fmt(r.return_date)}{r.tech ? ` (${r.tech})` : ""}</>}
              {kind === "active" && <>{r.status.replace("_", " ")}{r.tech ? ` — ${r.tech}` : ""}{r.scheduled ? ` · scheduled ${fmt(r.scheduled)}` : ""}</>}
              {kind === "new" && <>Received {fmt(r.created)} — not yet scheduled</>}
            </div>
            {r.proof_url && <a href={r.proof_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-black text-[#00C4CC] underline"><Camera weight="fill" size={12}/> {r.photos} after photo{r.photos === 1 ? "" : "s"}</a>}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function ActivityReportPublic() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    fetch(`${API}/api/public/activity-report/${token}`).then(async r => { if (!r.ok) throw new Error((await r.json()).detail || "Not found"); setD(await r.json()); }).catch(e => setErr(e.message));
  }, [token]);
  if (err) return <div className="min-h-screen flex items-center justify-center p-6 text-center font-bold" data-testid="activity-error">{err}</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center text-sm font-bold text-slate-500">Loading report…</div>;
  return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="activity-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3">
          {d.logo_url ? <img src={`${API}${d.logo_url}`} alt="" className="h-10 w-10 object-contain"/> : <Wrench weight="fill" size={28} className="text-[#00C4CC]"/>}
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{d.company || "Field Radar"} · Activity report</div>
          <button onClick={() => window.print()} className="ml-auto h-9 px-3 border-2 border-slate-900 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 print:hidden" data-testid="activity-print"><Printer weight="bold" size={14}/> Print / PDF</button>
        </div>
        <h1 className="mt-4 font-display font-black text-3xl sm:text-5xl uppercase tracking-tight" data-testid="activity-title">{d.label}</h1>
        <p className="text-sm font-bold text-slate-600">Last {d.days} days · since {fmt(d.since)} · prepared {new Date(d.created_at).toLocaleDateString()}{d.by ? ` by ${d.by}` : ""}</p>
        <div className="mt-4 grid grid-cols-4 gap-2 text-center">
          {[["Completed", d.counts.completed, "#059669"], ["Returning", d.counts.returning, "#f97316"], ["In progress", d.counts.active, "#0891b2"], ["New", d.counts.new, "#64748b"]].map(([l, n, c]) => (
            <div key={l} className="border-2 border-slate-900 bg-white p-2"><div className="font-display font-black text-2xl" style={{ color: c }}>{n}</div><div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{l}</div></div>
          ))}
        </div>
        <Section title="Completed" rows={d.completed} kind="completed" icon={CheckCircle} color="#059669"/>
        <Section title="Return visits scheduled" rows={d.returning} kind="returning" icon={ArrowUUpLeft} color="#f97316"/>
        <Section title="In progress / scheduled" rows={d.active} kind="active" icon={Wrench} color="#0891b2"/>
        <Section title="New this period" rows={d.new} kind="new" icon={Wrench} color="#64748b"/>
        <p className="mt-8 text-[11px] text-slate-400 font-medium">Read-only link · expires {fmt(d.expires_at)}</p>
      </div>
    </div>
  );
}
